import { VideoData } from '@lumen5/luminary';

import { intToHex } from 'js/src/libs/color-utils';

/**
 * Resolves theme reference strings (e.g. `themeRef_colors_N`) in an ECharts config tree into
 * real hex colors using the current video template's color palette.
 *
 * Used by thumbnails / previews which render through vanilla ECharts and don't go through the
 * luminary ChartRenderer. On the canvas, the chart asset uses the same theme reference strings
 * and luminary resolves them via its template-color pipeline (same "color shuffle" mechanism
 * other assets use).
 */

const NEAR_WHITE_CHANNEL_THRESHOLD = 240;

/**
 * True if a color's R, G, B channels are all >= 240. Used to filter out whites/near-whites
 * from the preview palette — the preview background is white, so we'd render invisible bars.
 */
export function isNearWhiteColor(color: number): boolean {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    return (
        r >= NEAR_WHITE_CHANNEL_THRESHOLD &&
        g >= NEAR_WHITE_CHANNEL_THRESHOLD &&
        b >= NEAR_WHITE_CHANNEL_THRESHOLD
    );
}

const FONT_REF_REGEX = /^themeRef_fonts_(\d+)$/;

/**
 * Inverse of `VideoData.templateFontsByIndex`. Returns the slot index for a
 * font reference string, or `null` if the value isn't a font reference.
 */
export function parseTemplateFontRefIndex(value: string | null | undefined): number | null {
    if (!value) return null;
    const m = FONT_REF_REGEX.exec(value);
    return m ? parseInt(m[1], 10) : null;
}

function resolveTemplateColor(value: string, templateColors: number[]): string {
    if (!VideoData.isTemplateReference(value)) return value;
    const idx = VideoData.getTemplateIndex(value);
    if (!Number.isFinite(idx) || templateColors.length === 0) return value;
    const wrapped = ((idx % templateColors.length) + templateColors.length) % templateColors.length;
    return intToHex(templateColors[wrapped]);
}

function resolveTemplateFont(idx: number, fontFamilies: string[]): string {
    if (fontFamilies.length === 0) return 'sans-serif';
    const wrapped = ((idx % fontFamilies.length) + fontFamilies.length) % fontFamilies.length;
    const family = fontFamilies[wrapped];
    return family ? `${family}, sans-serif` : 'sans-serif';
}

function resolveTemplateString(
    value: string,
    templateColors: number[],
    fontFamilies: string[],
): string {
    const fontIdx = parseTemplateFontRefIndex(value);
    if (fontIdx !== null) return resolveTemplateFont(fontIdx, fontFamilies);
    return resolveTemplateColor(value, templateColors);
}

/**
 * Resolve a single string value — returns the hex color if it's a template color reference,
 * otherwise returns the value unchanged.
 */
export function resolveThemeRefString(value: string, templateColors: number[]): string {
    return resolveTemplateColor(value, templateColors);
}

/**
 * Walks the config tree and returns a new tree with all template color and font references
 * resolved against the current template. Functions (formatters, animationDelay callbacks) are
 * preserved by reference. `fontFamilies[N]` should be the loaded font-family name for
 * `themeRef_fonts_N`.
 */
export function resolveThemeRefs<T>(node: T, templateColors: number[], fontFamilies: string[]): T {
    if (typeof node === 'string') {
        return resolveTemplateString(node, templateColors, fontFamilies) as unknown as T;
    }
    if (typeof node === 'function' || node === null || node === undefined) {
        return node;
    }
    if (Array.isArray(node)) {
        return node.map((item) =>
            resolveThemeRefs(item, templateColors, fontFamilies),
        ) as unknown as T;
    }
    if (typeof node === 'object') {
        const result: Record<string, unknown> = {};
        for (const key of Object.keys(node as Record<string, unknown>)) {
            result[key] = resolveThemeRefs(
                (node as Record<string, unknown>)[key],
                templateColors,
                fontFamilies,
            );
        }
        return result as T;
    }
    return node;
}
